import React, { createContext, useContext, useState, useEffect } from 'react';

// Use relative URL for API calls - works on any domain
const API_URL = '';

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Try to get token from localStorage as fallback
      const storedToken = localStorage.getItem('access_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Clear invalid token
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setUser(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      // Handle response based on status
      if (!response.ok) {
        // For error responses, try to get the error message
        let errorMessage = 'Invalid email or password';
        try {
          const errorData = await response.json();
          errorMessage = formatApiErrorDetail(errorData.detail);
        } catch {
          // If we can't parse the error, use status-based message
          if (response.status === 401) {
            errorMessage = 'Invalid email or password';
          } else if (response.status === 403) {
            errorMessage = 'Account deactivated. Contact admin.';
          } else if (response.status === 429) {
            errorMessage = 'Too many failed attempts. Try again later.';
          }
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Store tokens in localStorage as fallback for browsers that block cookies
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      // Handle network errors (Load failed, Failed to fetch, etc.)
      const errorMsg = error.message || '';
      if (errorMsg.includes('Load failed') || errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError') || errorMsg.includes('Network request failed')) {
        return { success: false, error: 'Connection error. Please check your internet and try again.' };
      }
      // Handle "body stream already read" errors gracefully
      if (errorMsg.includes('body stream') || errorMsg.includes('already read') || errorMsg.includes('already used')) {
        return { success: false, error: 'Invalid email or password' };
      }
      return { success: false, error: error.message || 'Login failed. Please try again.' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(formatApiErrorDetail(data.detail));
      }
      
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(false);
    }
  };

  const completeOnboarding = async (onboardingData) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(onboardingData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(formatApiErrorDetail(data.detail));
      }
      
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const refreshToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    completeOnboarding,
    refreshToken,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    needsOnboarding: user && !user.onboarding_completed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
